import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Client, DeviceConfigurationDataOut } from '../api/api-reference';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { SignalRService } from '../services/signalr.service';
import { SignalRBaseComponent } from '../services/signalr-base.component';
import { ConfirmDialogComponent } from '../modules/core/components/confirm-dialog/confirm-dialog.component';
import { SetupOptionDialogComponent } from '../modules/core/components/setup-option-dialog/setup-option-dialog.component';


type DeviceConfigurationStepDisplayItem =
  | { type: 'step'; step: DeviceConfigurationDataOut }
  | { type: 'group'; groupName: string; steps: DeviceConfigurationDataOut[] };

@Component({
  selector: 'app-device-setup',
  standalone: true,
  imports: [TranslateModule, CommonModule, AngularSvgIconModule, ConfirmDialogComponent, SetupOptionDialogComponent],
  templateUrl: './device-setup.component.html',
  styleUrl: './device-setup.component.scss'
})
export class DeviceSetupComponent extends SignalRBaseComponent implements OnChanges {
  @Input() deviceNumber: string = "";
  @Input() row: any;
  @Output() closeSignal = new EventEmitter<boolean>();

  steps: DeviceConfigurationDataOut[] = [];
  stepDisplayItems: DeviceConfigurationStepDisplayItem[] = [];

  isSetupModalOpen = false;
  isInitializing = false;
  isReconfigureDisabledFlag = false;
  isSetupOptionModalOpen = false;
  isForceFullSetup = false;
  canRunPartialSetup = false;

  percentCompleted = 0;

  private readonly EVENT_NAME = 'DeviceConfigurationUpdated';

  @ViewChild('stepsBody') private stepsBody?: ElementRef<HTMLElement>;

  constructor(
    private toastr: ToastrService,
    private client: Client,
    signalRService: SignalRService,
    private cdr: ChangeDetectorRef,
  ) {
    super(signalRService);
  }

  override async ngOnInit(): Promise<void> {
    await super.ngOnInit();
    this.getDeviceConfiguration();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['row'] && this.row) {
      this.checkInitializationStatus();
      this.updateReconfigureDisabledFlag();
    }

    if (changes['deviceNumber'] && !changes['deviceNumber'].firstChange) {
      const previousDeviceNumber = changes['deviceNumber'].previousValue;
      const currentDeviceNumber = changes['deviceNumber'].currentValue;

      if (previousDeviceNumber && previousDeviceNumber !== currentDeviceNumber) {
        this.switchDevice(previousDeviceNumber, currentDeviceNumber);
      }
    }
  }

  protected override async registerHandlers(): Promise<void> {
    // Register handler for the new event format (with deviceNumber parameter)
    this.registerSignalREvent(
      this.EVENT_NAME,
      (deviceNumber: string, setupSteps: DeviceConfigurationDataOut[]) => {
        if (this.isDestroyed) return;

        if (deviceNumber === this.deviceNumber) {
          this.handleDeviceUpdate(setupSteps);
        }
      }
    );

    // Register handler for the old event format (backward compatibility)
    this.registerSignalREvent(
      this.deviceNumber,
      (setupSteps: DeviceConfigurationDataOut[]) => {
        if (this.isDestroyed) return;
        this.handleDeviceUpdate(setupSteps);
      }
    );

    // Subscribe to device-specific updates
    await this.subscribeToDevice(this.deviceNumber);
  }

  private checkInitializationStatus(): void {
    if (this.row) {
      const status = this.row.configurationStatus;
      this.isInitializing = status === 'Initializing' || status === 'Reconfiguring';
    }
  }

  private handleDeviceUpdate(setupSteps: DeviceConfigurationDataOut[]): void {
    if (this.isDestroyed || !setupSteps || setupSteps.length === 0) {
      return;
    }
    const wasEmpty = this.steps.length === 0;
    this.steps = [...setupSteps];
    this.updateStepDisplayItems();

    if (wasEmpty) {
      this.isInitializing = false;
    }
    let percents = setupSteps[setupSteps.length - 1].percentCompleted;

    if (percents) {
      this.percentCompleted = percents;
    } else {
      // Use a minimum percentage to show some progress
      this.percentCompleted = 1;
    }
    this.cdr.detectChanges();
    this.scrollToBottom();
  }

  private scrollToBottom(): void {
    const el = this.stepsBody?.nativeElement;
    if (!el) return;
    requestAnimationFrame(() => el.scrollTop = el.scrollHeight);
  }

  private async switchDevice(oldDeviceNumber: string, newDeviceNumber: string): Promise<void> {
    if (this.isDestroyed) return;
    try {
      // Remove old event handlers
      this.removeSignalREvent(this.EVENT_NAME);
      this.removeSignalREvent(oldDeviceNumber);
      // Unsubscribe from old device
      await this.unsubscribeFromDevice(oldDeviceNumber);
      // Re-initialize handlers for new device
      await this.registerHandlers();
      // Fetch new device configuration
      this.getDeviceConfiguration();
    } catch (err) {
      console.error('[DeviceSetup] Failed to switch device subscription:', err);
    }
  }

  getDeviceConfiguration() {
    if (this.isDestroyed) return;
    this.client.queryDeviceConfiguration(this.deviceNumber).subscribe({
      next: (response) => {
        if (this.isDestroyed) return;

        if (response && response.deviceConfigurationSteps!.length > 0) {
          this.steps = response.deviceConfigurationSteps!;
          this.isInitializing = false;
          this.canRunPartialSetup = response.canRunPartialSetup;
          this.handleDeviceUpdate(this.steps);
        } else {
          this.isInitializing = true;
        }
      },
      error: (err) => {
        if (this.isDestroyed) return;
        this.toastr.error(err);
      }
    });
  }

  toggleModal() {
    this.closeSignal.emit();
  }

  confirmSetup() {
    if (this.isDestroyed) return;
    this.client.setupDevice(this.deviceNumber, this.isForceFullSetup).subscribe({
      next: (r) => {
        if (this.isDestroyed) return;
        this.toastr.success(r);
        this.isInitializing = true;
        this.steps = [];
        this.stepDisplayItems = [];
        this.toggleModal();
      },
      error: (err) => {
        if (this.isDestroyed) return;
        this.toastr.error(err);
      },
    });
    this.isSetupModalOpen = false;
  }

  cancelSetup() {
    this.isSetupModalOpen = false;
  }

  private updateReconfigureDisabledFlag(): void {
    if (!this.row) {
      this.isReconfigureDisabledFlag = false;
      return;
    }

    const status = this.row.configurationStatus;
    this.isReconfigureDisabledFlag = status === 'Reconfiguring' || status === 'Initializing';
  }

  private updateStepDisplayItems(): void {
    const steps = this.steps as DeviceConfigurationDataOut[];

    const renderedGroups = new Set<string>();
    this.stepDisplayItems = steps.reduce<DeviceConfigurationStepDisplayItem[]>((items, step) => {
      const groupName = this.getStepGroupName(step);

      if (!groupName) {
        items.push({ type: 'step', step });
        return items;
      }

      if (!renderedGroups.has(groupName)) {
        renderedGroups.add(groupName);
        items.push({
          type: 'group',
          groupName,
          steps: steps.filter(groupStep => this.getStepGroupName(groupStep) === groupName)
        });
      }

      return items;
    }, []);
  }

  private getStepGroupName(step: DeviceConfigurationDataOut): string | undefined {
    const groupName = step.groupName?.trim();
    return groupName || undefined;
  }

  getStepStatusClass(status: string | undefined): string {
    switch (status) {
      case 'Success':
        return 'step-success';
      case 'Configured':
        return 'step-success';
      case 'Deactivated':
        return 'step-success';
      case 'Failed':
        return 'step-failed';
      case 'NotSupported':
        return 'step-unsupported';
      default:
        return '';
    }
  }

  getStepStatusSuffix(status: string | undefined): string {
    switch (status) {
      case 'Success':
        return 'Ok';
      case 'Configured':
        return 'Configured';
      case 'Deactivated':
        return 'Deactivated';
      case 'Failed':
        return 'Fail';
      case 'NotSupported':
        return 'Skipped';
      case "AlreadyConfigured":
        return 'Previously Configured';
      default:
        return '';
    }
  }
}
